import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { LinkedInEntityRefDto, TriggerResultDto } from '@/types/dto';

type Result = { success: boolean; message: string } | null;

type TriggerGroupProps = {
  title: string;
  buttons: Array<{ key: string; label: string; primary?: boolean }>;
  onTrigger: (key: string) => void;
  busy: boolean;
};

function TriggerGroup({ title, buttons, onTrigger, busy }: TriggerGroupProps) {
  return (
    <Card>
      <h2 className="text-xl font-semibold mb-3">{title}</h2>
      <div className="flex flex-wrap gap-2">
        {buttons.map((b) => (
          <Button
            key={b.key}
            variant={b.primary ? 'primary' : 'default'}
            disabled={busy}
            onClick={() => onTrigger(b.key)}
          >
            {b.primary ? '▶' : '↻'} {b.label}
          </Button>
        ))}
      </div>
    </Card>
  );
}

export function TriggerPanel({ entities }: { entities: LinkedInEntityRefDto[] }) {
  const qc = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const [slug, setSlug] = useState('');

  const trigger = useMutation({
    mutationFn: (jobKey: string) =>
      api.post<TriggerResultDto>(`/api/debug/triggers/${encodeURIComponent(jobKey)}`),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['debug'] });
    },
    onError: (e: Error) => setResult({ success: false, message: e.message }),
  });

  const triggerLinkedin = useMutation({
    mutationFn: (s: string) =>
      api.post<TriggerResultDto>(`/api/debug/triggers/linkedin-company?slug=${encodeURIComponent(s)}`),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['debug'] });
    },
    onError: (e: Error) => setResult({ success: false, message: e.message }),
  });

  const busy = trigger.isPending || triggerLinkedin.isPending;
  const run = (k: string) => { setResult(null); trigger.mutate(k); };

  return (
    <div className="space-y-4">
      <TriggerGroup
        title="LinkedIn Company Profile"
        buttons={[
          { key: 'linkedin-company-t1', label: 'Tier 1 — 4h', primary: true },
          { key: 'linkedin-company-t2', label: 'Tier 2 — 24h' },
          { key: 'linkedin-company-t3', label: 'Tier 3 — 72h' },
        ]}
        onTrigger={run}
        busy={busy}
      />
      <TriggerGroup
        title="X / Twitter Posts"
        buttons={[{ key: 'x-posts', label: 'Industry analysts + company handles — daily', primary: true }]}
        onTrigger={run}
        busy={busy}
      />
      <TriggerGroup
        title="Google SERP Macro Scans"
        buttons={[
          { key: 'serp-t1', label: 'Tier 1 — daily 06:30', primary: true },
          { key: 'serp-t2', label: 'Tier 2 — daily 08:30' },
        ]}
        onTrigger={run}
        busy={busy}
      />
      <TriggerGroup
        title="Publication RSS Feeds"
        buttons={[{ key: 'publications', label: 'All publications — 6h', primary: true }]}
        onTrigger={run}
        busy={busy}
      />
      <TriggerGroup
        title="Processing + AI"
        buttons={[
          { key: 'extraction', label: 'Extraction (raw → news_items)', primary: true },
          { key: 'article-download', label: 'Article Download (150/batch, 8-concurrent)', primary: true },
          { key: 'scoring', label: 'Claude Scoring (200/run, 4-parallel)', primary: true },
          { key: 'bd-recovery', label: 'BD Recovery' },
        ]}
        onTrigger={run}
        busy={busy}
      />
      <TriggerGroup
        title="Synthesis + Stocks"
        buttons={[
          { key: 'daily-brief', label: 'Daily Brief (per-theme Claude summaries + overall)', primary: true },
          { key: 'stock-prices', label: 'Fetch Stock Prices (Yahoo EOD)', primary: true },
          { key: 'stock-analysis', label: 'Stock Analysis (Claude)', primary: true },
        ]}
        onTrigger={run}
        busy={busy}
      />

      <Card>
        <h2 className="text-xl font-semibold mb-3">Single Entity — LinkedIn Company</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            className="bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11 min-w-[16rem]"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          >
            <option value="">— pick an entity —</option>
            {entities.map((e) => (
              <option key={e.linkedInSlug} value={e.linkedInSlug}>
                {e.name} ({e.linkedInSlug})
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            disabled={busy || !slug}
            onClick={() => { setResult(null); triggerLinkedin.mutate(slug); }}
          >
            ▶ Scrape
          </Button>
        </div>
      </Card>

      {result && (
        <Card
          className={cn(
            'border-l-[3px]',
            result.success ? 'border-l-accent' : 'border-l-accent-danger'
          )}
        >
          {result.message}
        </Card>
      )}
    </div>
  );
}
